import { useLingui } from '@lingui/react/macro';
import { Object as FabricObject } from 'fabric/fabric-impl';
import { Plus, RotateCw, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toFiniteNumber } from 'shared/utils';
import { round } from 'shared/utils/round';
import history from 'modules/overlay-canvas/addons/history';
import UDIconButton from 'ud-ui/components/icon-button';
import UDText from 'ud-ui/components/text';
import {
  buildLinearGradient,
  GradientStop,
  LinearGradientModel,
  MIN_GRADIENT_STOPS,
  normalizeGradientAngle,
  readLinearGradient,
  withStopInWidestGap,
} from 'shared/fabric';
import ColorPicker from 'modules/template-editor/ui/components/elements/color-picker';
import Input, { IconWrapper } from 'modules/template-editor/ui/components/elements/input';
import { SubItem } from '../styles';
import { useEditorManager } from 'modules/template-editor/ui/context';

interface GradientFillProps {
  object: FabricObject;
}

/**
 * Линейный градиент фигуры: угол как в CSS и точки (цвет + положение). Модель читается из `object.fill`; `fill`
 * переписывается только правкой — нетронутый градиент каталога сохраняется как был.
 */
function GradientFill(props: GradientFillProps) {
  const { object } = props;
  const { t } = useLingui();
  const manager = useEditorManager();
  const [model, setModel] = useState(() => readModel(object));

  useEffect(() => {
    setModel(readModel(object));
  }, [object]);

  if (!model) {
    return <UDText type={'caption-1'}>{t`Такой градиент редактор пока не меняет — его можно только заменить`}</UDText>;
  }

  const apply = (next: LinearGradientModel) => {
    object.set('fill', buildLinearGradient(next, object.width ?? 0, object.height ?? 0));
    setModel(next);
    manager?.render();
    history.saveState();
  };

  const onChangeAngle = (value: string) => {
    const angle = normalizeGradientAngle(toFiniteNumber(value, model.angle));
    if (angle === model.angle) return;
    apply({ ...model, angle });
  };

  const updateStop = (index: number, patch: Partial<GradientStop>) => {
    const stops = model.stops.map((stop, i) => (i === index ? { ...stop, ...patch } : stop));
    apply({ ...model, stops: [...stops].sort((a, b) => a.offset - b.offset) });
  };

  const onChangeOffset = (index: number, value: string) => {
    const current = model.stops[index].offset;
    const offset = Math.min(100, Math.max(0, toFiniteNumber(value, current * 100))) / 100;
    if (offset === current) return;
    updateStop(index, { offset });
  };

  const onRemoveStop = (index: number) => {
    apply({ ...model, stops: model.stops.filter((_, i) => i !== index) });
  };

  const onAddStop = () => {
    apply({ ...model, stops: withStopInWidestGap(model.stops) });
  };

  return (
    <>
      <SubItem style={{ justifyContent: 'space-between' }}>
        <span>{t`Угол`}</span>
        <Input
          numeric
          unit={'°'}
          icon={
            <IconWrapper>
              <RotateCw size={14} />
            </IconWrapper>
          }
          style={{ width: 127 }}
          value={round(model.angle, 1).toString()}
          onSubmit={onChangeAngle}
          onBlur={onChangeAngle}
        />
      </SubItem>
      <SubItem style={{ justifyContent: 'space-between' }}>
        <span>{t`Точки`}</span>
        <UDIconButton
          variant="ghost"
          size="sm"
          icon={<Plus />}
          aria-label={t`Добавить точку`}
          tooltip={t`Добавить точку`}
          onClick={onAddStop}
        />
      </SubItem>
      {model.stops.map((stop, index) => (
        <SubItem key={index} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
          <ColorPicker color={stop.color} onChange={(color) => updateStop(index, { color })} showAlpha />
          <SubItem style={{ justifyContent: 'space-between' }}>
            <Input
              numeric
              unit={'%'}
              style={{ width: 127 }}
              value={round(stop.offset * 100, 1).toString()}
              onSubmit={(value) => onChangeOffset(index, value)}
              onBlur={(value) => onChangeOffset(index, value)}
            />
            <UDIconButton
              variant="ghost"
              size="sm"
              icon={<Trash2 />}
              aria-label={t`Удалить точку`}
              tooltip={t`Удалить точку`}
              disabled={model.stops.length <= MIN_GRADIENT_STOPS}
              onClick={() => onRemoveStop(index)}
            />
          </SubItem>
        </SubItem>
      ))}
    </>
  );
}

export default GradientFill;

function readModel(object: FabricObject) {
  return readLinearGradient(object.fill, object.width ?? 0, object.height ?? 0);
}
